'use client';

import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useWallet } from './use-wallet';

const POLL_INTERVAL = 5 * 60 * 1000; // 5 min fallback — real-time delivery via SSE

export type NotificationType = 'bought_out' | 'expiry_1d' | 'expiry_1h' | 'chat_mention';

export interface Notification {
  id: number;
  type: NotificationType;
  title: string;
  message: string;
  pool_id: string;
  pool_symbol: string | null;
  pool_image_url: string | null;
  seat_index: number | null;
  seat_indices: number[] | null;
  amount: string | null;
  tx_hash: string | null;
  buyer_address: string | null;
  href: string | null;
  read: boolean;
  created_at: number;
  room: string | null;
  sender_address: string | null;
  /** Chat-mention only: id of the chat message that triggered the alert.
   *  Used to build a `?chat_msg=…` deep-link into the board chat panel. */
  message_id: number | null;
}

interface NotificationsResponse {
  notifications: Notification[];
  unreadCount: number;
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  try {
    const privyToken = document.cookie
      .split('; ')
      .find((row) => row.startsWith('privy-token='))
      ?.split('=')[1];
    if (privyToken) {
      headers['Authorization'] = `Bearer ${privyToken}`;
    }
  } catch {
    // Cookie access may fail in some contexts
  }
  return headers;
}

/**
 * Hook for managing notifications via the DB-backed API.
 *
 * Polls GET /api/notifications for the current user. All notifications
 * (buyouts, expiry, chat mentions) are created server-side.
 */
export function useNotifications() {
  const { address, isConnected, isAddressLinked } = useWallet();
  const queryClient = useQueryClient();

  // Address segment is lowercased so a wallet that emits its own
  // address checksummed on one connect and lowercase on another
  // (or a wagmi/Privy reconnection with different casing) can't
  // produce two distinct cache observers + two GETs against the
  // same effective resource. Mirrors the server-side cache key
  // (`notifications:${addressLower}:…`).
  const queryKey = ['db-notifications', address?.toLowerCase() ?? null];

  const { data, isLoading } = useQuery<NotificationsResponse>({
    queryKey,
    queryFn: async (): Promise<NotificationsResponse> => {
      if (!address) return { notifications: [], unreadCount: 0 };
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/notifications?address=${address}`, { headers });
      if (!res.ok) return { notifications: [], unreadCount: 0 };
      const json = await res.json();
      return {
        notifications: json.notifications ?? [],
        unreadCount: json.unreadCount ?? 0,
      };
    },
    // Gated on `isAddressLinked` — `requireAuth` on the server returns 403
    // whenever the `?address=` param isn't in the authenticated Privy
    // user's linked wallets. Without this guard, switching to an external
    // (e.g. MetaMask) wallet that the Privy session doesn't know about
    // fires a poll every 5 min (and once immediately) that always 403s.
    enabled: isConnected && !!address && isAddressLinked,
    refetchInterval: POLL_INTERVAL,
    staleTime: POLL_INTERVAL / 2,
  });

  // Patch the cached notification list in place. Used by every mutation
  // below for an instant UI flip — without it the bell / row styling
  // would freeze until the next 5-minute poll. The patcher runs through
  // a referentially-new array so React re-renders, and recomputes
  // `unreadCount` from the patched rows so the badge stays in sync.
  const patchCache = useCallback(
    (mutator: (n: Notification) => Notification | null) => {
      queryClient.setQueryData<NotificationsResponse>(queryKey, (prev) => {
        if (!prev) return prev;
        const next: Notification[] = [];
        for (const n of prev.notifications) {
          const m = mutator(n);
          if (m === null) continue;
          next.push(m);
        }
        const unread = next.reduce((acc, n) => acc + (n.read ? 0 : 1), 0);
        return { notifications: next, unreadCount: unread };
      });
    },
    [queryClient, queryKey],
  );

  const handleMarkAsRead = useCallback(
    async (notificationId: number | string) => {
      const id = typeof notificationId === 'string' ? parseInt(notificationId, 10) : notificationId;
      if (isNaN(id) || !address) return;

      patchCache((n) => (n.id === id && !n.read ? { ...n, read: true } : n));

      const headers = await getAuthHeaders();
      await fetch(`/api/notifications/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ address }),
      });

      queryClient.invalidateQueries({ queryKey });
    },
    [address, queryClient, queryKey, patchCache],
  );

  const handleMarkAllAsRead = useCallback(async () => {
    if (!address) return;

    patchCache((n) => (n.read ? n : { ...n, read: true }));

    const headers = await getAuthHeaders();
    await fetch('/api/notifications/mark-all-read', {
      method: 'POST',
      headers,
      body: JSON.stringify({ address }),
    });
    queryClient.invalidateQueries({ queryKey });
  }, [address, queryClient, queryKey, patchCache]);

  const handleDelete = useCallback(
    async (notificationId: number | string) => {
      const id = typeof notificationId === 'string' ? parseInt(notificationId, 10) : notificationId;
      if (isNaN(id) || !address) return;

      patchCache((n) => (n.id === id ? null : n));

      const headers = await getAuthHeaders();
      await fetch(`/api/notifications/${id}?address=${address}`, {
        method: 'DELETE',
        headers,
      });

      queryClient.invalidateQueries({ queryKey });
    },
    [address, queryClient, queryKey, patchCache],
  );

  const handleMarkAsUnread = useCallback(
    async (notificationId: number | string) => {
      const id = typeof notificationId === 'string' ? parseInt(notificationId, 10) : notificationId;
      if (isNaN(id) || !address) return;

      patchCache((n) => (n.id === id && n.read ? { ...n, read: false } : n));

      const headers = await getAuthHeaders();
      await fetch(`/api/notifications/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ address, read: false }),
      });

      queryClient.invalidateQueries({ queryKey });
    },
    [address, queryClient, queryKey, patchCache],
  );

  const notifications = useMemo(() => data?.notifications ?? [], [data]);
  const unreadCount = data?.unreadCount ?? 0;

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead: handleMarkAsRead,
    markAsUnread: handleMarkAsUnread,
    markAllAsRead: handleMarkAllAsRead,
    deleteNotification: handleDelete,
  };
}
